import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Home as HomeIcon,
  Business as BusinessIcon,
  Cloud as CloudIcon,
  Copyright as CopyrightIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Link as LinkIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import './MentionsLegales.css';

const MentionsLegales = () => {
  const legalSections = [
    {
      icon: <BusinessIcon />,
      title: 'Éditeur du site',
      content: {
        nom: 'Mission Sport',
        description: 'Plateforme de mise en relation entre professionnels du sport et organisations sportives',
        responsable: 'Équipe Mission Sport',
        email: 'contact@missionsport.fr',
        adresse: '123 Avenue du Sport, 75001 Paris, France',
        siret: 'XXX XXX XXX 00001',
        forme: 'SAS (Société par Actions Simplifiée)'
      }
    },
    {
      icon: <CloudIcon />,
      title: 'Hébergement',
      content: {
        nom: 'Amazon Web Services (AWS)',
        adresse: 'Amazon Web Services EMEA SARL, 38 Avenue John F. Kennedy, L-1855 Luxembourg',
        site: 'https://aws.amazon.com',
        description: 'Infrastructure cloud sécurisée avec centres de données certifiés ISO 27001'
      }
    },
    {
      icon: <CopyrightIcon />,
      title: 'Propriété intellectuelle',
      content: {
        description: 'Tous les éléments du site Mission Sport (textes, images, logos, graphismes, vidéos, sons, animations, bases de données, programmes, charte graphique) sont protégés par les dispositions du Code de la propriété intellectuelle et par les conventions internationales relatives au droit d\'auteur.',
        interdiction: 'Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite, sauf autorisation écrite préalable de Mission Sport.',
        marques: 'Les marques et logos présents sur le site sont déposés par Mission Sport ou par des tiers ayant autorisé Mission Sport à les utiliser.'
      }
    },
    {
      icon: <SecurityIcon />,
      title: 'Responsabilité',
      content: {
        limitation: 'Mission Sport s\'efforce de fournir sur le site des informations aussi précises que possible. Toutefois, elle ne pourra être tenue responsable des omissions, des inexactitudes et des carences dans la mise à jour, qu\'elles soient de son fait ou du fait des tiers partenaires qui lui fournissent ces informations.',
        interruption: 'Mission Sport ne pourra être tenue responsable des dommages directs et indirects causés au matériel de l\'utilisateur, lors de l\'accès au site, et résultant soit de l\'utilisation d\'un matériel ne répondant pas aux spécifications indiquées, soit de l\'apparition d\'un bug ou d\'une incompatibilité.',
        disponibilite: 'Mission Sport se réserve le droit d\'interrompre, de suspendre momentanément ou de modifier sans préavis l\'accès à tout ou partie du site pour des raisons de maintenance ou pour tout autre motif jugé nécessaire.'
      }
    },
    {
      icon: <ShieldIcon />,
      title: 'Protection des données personnelles',
      content: {
        collecte: 'Mission Sport collecte des données personnelles dans le cadre de l\'inscription, de l\'utilisation des services et des formulaires de contact. Ces données sont nécessaires au fonctionnement de la plateforme.',
        utilisation: 'Les données collectées sont utilisées pour : la gestion des comptes utilisateurs, la mise en relation entre professionnels et organisations, l\'amélioration des services, la communication d\'informations pertinentes.',
        droits: 'Conformément au RGPD, vous disposez d\'un droit d\'accès, de rectification, de suppression, de portabilité et d\'opposition concernant vos données personnelles. Pour exercer ces droits, contactez-nous à privacy@missionsport.fr.',
        conservation: 'Les données sont conservées pendant la durée nécessaire aux finalités pour lesquelles elles ont été collectées, dans le respect des obligations légales.'
      }
    },
    {
      icon: <LinkIcon />,
      title: 'Liens externes',
      content: {
        responsabilite: 'Le site Mission Sport peut contenir des liens hypertextes vers d\'autres sites présents sur le réseau Internet. Les liens vers ces autres ressources vous font quitter le site Mission Sport.',
        controle: 'Il est possible que ces liens soient créés sans autorisation expresse de Mission Sport. Cette dernière ne peut donc être tenue responsable du contenu des sites en question.',
        verification: 'L\'utilisateur reconnaît que Mission Sport n\'a aucun contrôle sur ces sites et ne peut être tenue responsable de leur contenu, de leur fonctionnement ou de l\'accès à ces sites.'
      }
    },
    {
      icon: <GavelIcon />,
      title: 'Loi applicable et juridiction',
      content: {
        loi: 'Les présentes mentions légales sont régies par la loi française. En cas de litige, et à défaut de résolution amiable, les tribunaux français seront seuls compétents.',
        juridiction: 'Tout litige en relation avec l\'utilisation du site Mission Sport est soumis au droit français. Il est fait attribution exclusive de juridiction aux tribunaux compétents de Paris.',
        mediation: 'En cas de litige, une solution amiable sera recherchée avant toute action judiciaire. À défaut, les tribunaux français seront seuls compétents pour connaître du litige.'
      }
    }
  ];

  return (
    <div className="mentions-page">
      <Helmet>
        <title>Mentions légales | Mission Sport</title>
        <meta 
          name="description" 
          content="Mentions légales du site Mission Sport : informations légales, éditeur, hébergement, propriété intellectuelle, responsabilité, protection des données personnelles."
        />
        <meta name="keywords" content="mentions légales, Mission Sport, éditeur, hébergement, propriété intellectuelle, RGPD, responsabilité" />
        <meta property="og:title" content="Mentions légales | Mission Sport" />
        <meta property="og:description" content="Mentions légales du site Mission Sport : informations légales, éditeur, hébergement, propriété intellectuelle, responsabilité, protection des données personnelles." />
        <meta property="og:url" content="https://missionsport.fr/mentions-legales" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Mentions légales | Mission Sport" />
        <meta name="twitter:description" content="Mentions légales du site Mission Sport : informations légales complètes." />
      </Helmet>

      <Container maxWidth="lg" className="mentions-container">
        {/* Breadcrumb */}
        <Box className="breadcrumb-section">
          <Breadcrumbs aria-label="breadcrumb">
            <Link 
              component={RouterLink} 
              to="/" 
              color="inherit" 
              className="breadcrumb-link"
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Accueil
            </Link>
            <Typography color="text.primary">Mentions légales</Typography>
          </Breadcrumbs>
        </Box>

        {/* Header */}
        <header className="mentions-header">
          <Box className="header-content">
            <GavelIcon className="header-icon" />
            <Typography variant="h1" className="mentions-title">
              Mentions légales
            </Typography>
            <Typography variant="h2" className="mentions-subtitle">
              Informations légales et réglementaires
            </Typography>
            <Typography variant="body1" className="mentions-intro">
              Conformément aux dispositions des articles 6-III et 19 de la Loi n°2004-575 du 21 juin 2004 
              pour la Confiance dans l'économie numérique, dite L.C.E.N., nous portons à la connaissance des 
              utilisateurs et visiteurs du site les présentes mentions légales.
            </Typography>
            <Typography variant="body2" className="last-update">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
            </Typography>
          </Box>
        </header>

        <main className="mentions-main">
          {/* Introduction */}
          <Paper className="intro-section" elevation={1}>
            <Typography variant="h3" className="section-title">
              À propos de ces mentions légales
            </Typography>
            <Typography variant="body1" className="section-text">
              Ces mentions légales ont pour objet de définir les modalités et conditions d'utilisation 
              du site Mission Sport, ainsi que les droits et obligations des parties dans ce cadre.
            </Typography>
            <Typography variant="body1" className="section-text">
              L'utilisation du site Mission Sport implique l'acceptation pleine et entière des conditions 
              générales d'utilisation décrites ci-après. Ces conditions d'utilisation sont susceptibles 
              d'être modifiées ou complétées à tout moment.
            </Typography>
          </Paper>

          {/* Sections légales */}
          <Box className="legal-sections">
            <Grid container spacing={3}>
              {legalSections.map((section, index) => (
                <Grid item xs={12} key={index}>
                  <Card className="legal-card" elevation={2}>
                    <CardContent>
                      <Box className="card-header">
                        <Box className="card-icon">
                          {section.icon}
                        </Box>
                        <Typography variant="h3" className="card-title">
                          {section.title}
                        </Typography>
                      </Box>
                      
                      <Box className="card-content">
                        {/* Éditeur du site */}
                        {section.title === 'Éditeur du site' && (
                          <>
                            <Typography variant="body1" className="content-item">
                              <strong>Dénomination sociale :</strong> {section.content.nom}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Description :</strong> {section.content.description}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Forme juridique :</strong> {section.content.forme}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>SIRET :</strong> {section.content.siret}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Responsable de publication :</strong> {section.content.responsable}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Email :</strong> <a href={`mailto:${section.content.email}`} className="contact-link">{section.content.email}</a>
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Adresse :</strong> {section.content.adresse}
                            </Typography>
                          </>
                        )}

                        {/* Hébergement */}
                        {section.title === 'Hébergement' && (
                          <>
                            <Typography variant="body1" className="content-item">
                              <strong>Hébergeur :</strong> {section.content.nom}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Adresse :</strong> {section.content.adresse}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Site web :</strong> <a href={section.content.site} target="_blank" rel="noopener noreferrer" className="contact-link">{section.content.site}</a>
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              {section.content.description}
                            </Typography>
                          </>
                        )}

                        {/* Propriété intellectuelle */}
                        {section.title === 'Propriété intellectuelle' && (
                          <>
                            <Typography variant="body1" className="content-item">
                              {section.content.description}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              {section.content.interdiction}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Marques et logos :</strong> {section.content.marques}
                            </Typography>
                          </>
                        )}

                        {/* Responsabilité */}
                        {section.title === 'Responsabilité' && (
                          <>
                            <Typography variant="body1" className="content-item">
                              <strong>Limitation de responsabilité :</strong> {section.content.limitation}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Interruptions de service :</strong> {section.content.interruption}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Disponibilité :</strong> {section.content.disponibilite}
                            </Typography>
                          </>
                        )}

                        {/* Protection des données */}
                        {section.title === 'Protection des données personnelles' && (
                          <>
                            <Typography variant="body1" className="content-item">
                              <strong>Collecte des données :</strong> {section.content.collecte}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Utilisation :</strong> {section.content.utilisation}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Vos droits :</strong> {section.content.droits}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Conservation :</strong> {section.content.conservation}
                            </Typography>
                            <Typography variant="body2" className="content-note">
                              Pour plus de détails, consultez notre <Link component={RouterLink} to="/privacy" className="contact-link">Politique de confidentialité</Link>.
                            </Typography>
                          </>
                        )}

                        {/* Liens externes */}
                        {section.title === 'Liens externes' && (
                          <>
                            <Typography variant="body1" className="content-item">
                              {section.content.responsabilite}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              {section.content.controle}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              {section.content.verification}
                            </Typography>
                          </>
                        )}

                        {/* Loi applicable */}
                        {section.title === 'Loi applicable et juridiction' && (
                          <>
                            <Typography variant="body1" className="content-item">
                              <strong>Loi applicable :</strong> {section.content.loi}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Juridiction compétente :</strong> {section.content.juridiction}
                            </Typography>
                            <Typography variant="body1" className="content-item">
                              <strong>Médiation :</strong> {section.content.mediation}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider className="main-divider" />

          {/* Contact */}
          <Paper className="contact-section" elevation={1}>
            <Box className="contact-header">
              <BusinessIcon className="contact-icon" />
              <Typography variant="h3" className="section-title">
                Nous contacter
              </Typography>
            </Box>
            <Typography variant="body1" className="section-text">
              Pour toute question relative aux présentes mentions légales ou à l'utilisation du site Mission Sport, 
              vous pouvez nous contacter :
            </Typography>
            <Box className="contact-info">
              <Typography variant="body1">
                <strong>Email :</strong> <a href="mailto:legal@missionsport.fr" className="contact-link">legal@missionsport.fr</a>
              </Typography>
              <Typography variant="body1">
                <strong>Formulaire :</strong> <Link component={RouterLink} to="/contact" className="contact-link">Page de contact</Link>
              </Typography>
              <Typography variant="body1">
                <strong>Courrier :</strong> Mission Sport - Service Juridique, 123 Avenue du Sport, 75001 Paris, France
              </Typography>
            </Box>
          </Paper>
        </main>
      </Container>
    </div>
  );
};

export default MentionsLegales;